import { useState, useEffect, useCallback } from 'react';
import api, { formatCurrency, formatDate } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const empty = { client_id:'', service_name:'', description:'', start_date:new Date().toISOString().slice(0,10), renewal_date:'', amount:0, status:'active' };

export default function Renewals() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows]       = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoad]    = useState(true);
  const [filter, setFilter]   = useState('');
  const [search, setSearch]   = useState('');
  const [modal, setModal]     = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm]       = useState(empty);
  const [saving, setSaving]   = useState(false);

  const fetch = useCallback(() => {
    setLoad(true);
    Promise.all([
      api.get('/renewals', { params:{ status:filter, search } }),
      api.get('/clients')
    ]).then(([r,c]) => { setRows(r.data.data||[]); setClients(c.data.data||[]); setLoad(false); })
     .catch(()=>setLoad(false));
  }, [filter, search]);

  useEffect(()=>{ const t=setTimeout(fetch,300); return ()=>clearTimeout(t); }, [fetch]);

  const openNew  = () => { setEditing(null); setForm(empty); setModal(true); };
  const openEdit = (r) => { setEditing(r); setForm({ client_id:r.client_id, service_name:r.service_name, description:r.description||'', start_date:r.start_date?.slice(0,10)||'', renewal_date:r.renewal_date?.slice(0,10)||'', amount:r.amount||0, status:r.status }); setModal(true); };

  const save = async (e) => {
    e.preventDefault(); setSaving(true);
    try {
      if (editing) { await api.put(`/renewals/${editing.id}`, form); toast.success('Renewal updated'); }
      else         { await api.post('/renewals', form);               toast.success('Renewal added'); }
      setModal(false); fetch();
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this renewal?')) return;
    await api.delete(`/renewals/${id}`); toast.success('Deleted'); fetch();
  };

  const dayColor = (d) => { const n=parseInt(d); if(n<0||n===0)return'var(--err)'; if(n<=7)return'var(--warn)'; return'var(--ok)'; };
  const dayText  = (d) => { const n=parseInt(d); if(n<0)return`${Math.abs(n)}d overdue`; if(n===0)return'Due today!'; return`${n}d left`; };
  const dayBadge = (d) => { const n=parseInt(d); if(n<=0)return'overdue'; if(n<=7)return'unpaid'; return'active'; };

  const alerts = rows.filter(r=>parseInt(r.days_until)>=0&&parseInt(r.days_until)<=7&&r.status==='active');

  const whatsapp = (r) => {
    const txt = `Hi ${r.client_name},\n\nYour *${r.service_name}* service is due for renewal on *${formatDate(r.renewal_date)}*.\nAmount: *${formatCurrency(r.amount)}*\n\nPlease contact us to renew.\n\n${user?.company_name||''}`;
    window.open(`https://wa.me/${r.client_phone?.replace(/\D/g,'')||''}?text=${encodeURIComponent(txt)}`, '_blank');
  };

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">Renewals</h2><p className="page-subtitle">Track subscriptions & service renewals</p></div>
        <div className="page-actions">
          <div className="search-box"><span>🔍</span><input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
          <button className="btn btn-primary" onClick={openNew}>+ Add Renewal</button>
        </div>
      </div>

      {alerts.length>0 && (
        <div style={{ marginBottom:16 }}>
          {alerts.slice(0,3).map(r=>(
            <div key={r.id} className="alert alert-warn">
              ⏰ <strong>{r.service_name}</strong> for <strong>{r.client_name}</strong> renews {parseInt(r.days_until)===0?'TODAY':`in ${r.days_until} day${r.days_until!=='1'?'s':''}`} — {formatDate(r.renewal_date)}
              {r.client_phone && <button className="btn btn-sm btn-warn" style={{ marginLeft:'auto' }} onClick={()=>whatsapp(r)}>💬 Remind</button>}
            </div>
          ))}
        </div>
      )}

      <div className="tabs">
        {['','active','expired','cancelled'].map(s=>(
          <div key={s} className={`tab ${filter===s?'active':''}`} onClick={()=>setFilter(s)}>
            {s?s.charAt(0).toUpperCase()+s.slice(1):'All'}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--text3)' }}>Loading…</div>
        : rows.length===0 ? (
          <div className="empty-state"><div className="empty-icon">🔄</div><h3>No renewals yet</h3>
            <p>Track client subscriptions and service renewals</p>
            <button className="btn btn-primary" onClick={openNew}>+ Add Renewal</button></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Number</th><th>Service</th><th>Client</th><th>Renewal Date</th><th>Amount</th><th>Days</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {rows.map(r=>(
                  <tr key={r.id}>
                    <td style={{ color:'var(--accent2)',fontWeight:600,fontSize:12 }}>{r.renewal_number||'—'}</td>
                    <td>
                      <div style={{ fontWeight:600 }}>{r.service_name}</div>
                      {r.description&&<div style={{ fontSize:11,color:'var(--text3)' }}>{r.description}</div>}
                    </td>
                    <td>{r.client_name}</td>
                    <td style={{ color:dayColor(r.days_until),fontWeight:600 }}>{formatDate(r.renewal_date)}</td>
                    <td>{formatCurrency(r.amount)}</td>
                    <td><span className={`badge badge-${dayBadge(r.days_until)}`}>{dayText(r.days_until)}</span></td>
                    <td><span className={`badge badge-${r.status}`}>{r.status}</span></td>
                    <td>
                      <div style={{ display:'flex',gap:4 }}>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>openEdit(r)} title="Edit">✏️</button>
                        {r.client_phone&&<button className="btn btn-sm btn-secondary btn-icon" onClick={()=>whatsapp(r)} title="WhatsApp">💬</button>}
                        {isAdmin&&<button className="btn btn-sm btn-danger btn-icon" onClick={()=>del(r.id)} title="Delete">🗑️</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&setModal(false)}>
          <div className="modal">
            <div className="modal-header">
              <h3 style={{ fontFamily:'Syne,sans-serif',fontWeight:700 }}>{editing?'Edit Renewal':'Add Renewal'}</h3>
              <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>setModal(false)}>✕</button>
            </div>
            <form onSubmit={save}>
              <div className="modal-body">
                <div className="form-group"><label className="form-label">Client *</label>
                  <select className="form-control" value={form.client_id} onChange={e=>setForm({...form,client_id:e.target.value})} required>
                    <option value="">— Select Client —</option>
                    {clients.map(c=><option key={c.id} value={c.id}>{c.name}</option>)}
                  </select></div>
                <div className="form-group"><label className="form-label">Service Name *</label>
                  <input className="form-control" value={form.service_name} onChange={e=>setForm({...form,service_name:e.target.value})} placeholder="e.g. Web Hosting, Domain, SEO Package" required /></div>
                <div className="form-group"><label className="form-label">Description</label>
                  <input className="form-control" value={form.description} onChange={e=>setForm({...form,description:e.target.value})} placeholder="Optional details…" /></div>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Start Date</label>
                    <input type="date" className="form-control" value={form.start_date} onChange={e=>setForm({...form,start_date:e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Renewal Date *</label>
                    <input type="date" className="form-control" value={form.renewal_date} onChange={e=>setForm({...form,renewal_date:e.target.value})} required /></div>
                </div>
                <div className="grid-2">
                  <div className="form-group"><label className="form-label">Amount (₹)</label>
                    <input type="number" min="0" step=".01" className="form-control" value={form.amount} onChange={e=>setForm({...form,amount:e.target.value})} /></div>
                  <div className="form-group"><label className="form-label">Status</label>
                    <select className="form-control" value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                      <option value="active">Active</option><option value="expired">Expired</option><option value="cancelled">Cancelled</option>
                    </select></div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={()=>setModal(false)}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>{saving?<span className="spinner"/>:(editing?'Update':'Add Renewal')}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
